/**
 * @fileoverview Document Detail Sheet
 *
 * Purpose: Right-side drawer (Sheet) showing full document detail
 * with tabs for Metadata, Extracted Content, Original File, and Timeline.
 *
 * @component
 */

import DOMPurify from "dompurify";
import { useState, useEffect, useMemo, Fragment } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import type { FunctionReturnType } from "convex/server";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/ui/composed/overlays/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { Separator } from "@/ui/primitives/separator";
import {
  Loader2,
  FileText,
  Brain,
  Clock,
  Eye,
  FileSearch,
  Download,
  AlertCircle,
  RotateCcw,
  FileSearch2,
  Mail,
} from "lucide-react";
import { Button } from "@/ui/primitives/button";
import { toast } from "sonner";
import { ProcessingStatusBadge } from "./ProcessingStatusBadge";
import { DocumentPreview } from "./DocumentPreview";
import { DocumentMetadataPanel } from "./DocumentMetadataPanel";
import { coerceStructuredMetadata } from "./structured-metadata-normalize";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import {
  exportToWord,
  exportToPdf,
  exportToExcel,
  type ExportSheet,
} from "@/utils/export";
import { saveAs } from "file-saver";
import { CATEGORY_LABELS } from "../constants";
import { formatFileSize, formatDate } from "../helpers";
import * as mammoth from "mammoth";
import * as XLSX from "xlsx";

export type DocumentDetailTab =
  "original" | "metadata" | "extracted" | "timeline";

/** Full document detail as returned by `getDocumentDetail` (non-null branch). */
type DocumentDetailData = NonNullable<
  FunctionReturnType<
    typeof api.jobs.gatherInformation.documents.queries.getDocumentDetail
  >
>;

interface DocumentDetailProps {
  documentId: Id<"documents"> | null;
  isViewer: boolean;
  open: boolean;
  onClose: () => void;
  defaultTab?: DocumentDetailTab;
}

/**
 * Slide-over sheet showing full document detail with tabbed content.
 */
export function DocumentDetail({
  documentId,
  isViewer,
  open,
  onClose,
  defaultTab = "original",
}: DocumentDetailProps) {
  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-4xl lg:max-w-6xl">
        {documentId && (
          <DocumentDetailContent
            documentId={documentId}
            isViewer={isViewer}
            defaultTab={defaultTab}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Inner Content (only renders when documentId is set)
// ---------------------------------------------------------------------------

function DocumentDetailContent({
  documentId,
  isViewer,
  defaultTab = "original",
}: {
  documentId: Id<"documents">;
  isViewer: boolean;
  defaultTab?: DocumentDetailTab;
}) {
  const { data: detail, isLoading } = useQuery(
    convexQuery(
      api.jobs.gatherInformation.documents.queries.getDocumentDetail,
      { documentId },
    ),
  );

  const { data: processingHistory } = useQuery(
    convexQuery(
      api.jobs.gatherInformation.extraction.queries
        .getDocumentProcessingHistory,
      {
        documentId,
      },
    ),
  );

  const { data: categoryExtractions } = useQuery(
    convexQuery(
      api.jobs.gatherInformation.extraction.queries
        .getDocumentCategoryExtractions,
      {
        documentId,
      },
    ),
  );

  const { mutateAsync: reprocessAll, isPending: isReprocessingAll } =
    useMutation({
      mutationFn: useConvexMutation(
        api.jobs.gatherInformation.documents.mutations.reprocessDocument,
      ),
    });
  const { mutateAsync: rerunMetadata, isPending: isRerunningMetadata } =
    useMutation({
      mutationFn: useConvexMutation(
        api.jobs.gatherInformation.documents.mutations.rerunMetadataExtraction,
      ),
    });
  const { mutateAsync: updateDocTitle } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.gatherInformation.documents.mutations.updateDocumentMetadata,
    ),
  });

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const docTitle = detail ? detail.documentType : undefined;

  const handleTitleSave = async () => {
    const trimmed = editTitle.trim();
    if (trimmed && trimmed !== docTitle) {
      try {
        await updateDocTitle({ documentId, documentType: trimmed });
        toast.success("Title updated");
      } catch {
        toast.error("Failed to update title");
      }
    }
    setIsEditingTitle(false);
  };

  const handleReprocessAll = async () => {
    try {
      await reprocessAll({ documentId });
      toast.success("Re-processing started");
    } catch {
      toast.error("Failed to start re-processing");
    }
  };

  const handleRerunMetadata = async () => {
    try {
      await rerunMetadata({ documentId });
      toast.success("Metadata extraction started");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to start metadata extraction",
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        Document not found or access denied.
      </div>
    );
  }

  return (
    <>
      <SheetHeader className="pb-4">
        <SheetTitle className="flex items-center gap-2 text-base">
          {isEditingTitle ? (
            <input
              autoFocus
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onBlur={handleTitleSave}
              onKeyDown={(e) => {
                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                if (e.key === "Escape") setIsEditingTitle(false);
              }}
              className="flex-1 border-b border-foreground/20 bg-transparent px-0 py-0.5 text-base font-semibold outline-none focus:border-foreground/40"
            />
          ) : (
            <span
              className={
                !isViewer
                  ? "cursor-pointer transition-colors hover:text-foreground/70"
                  : ""
              }
              onClick={() => {
                if (isViewer) return;
                setEditTitle(docTitle || detail.fileName);
                setIsEditingTitle(true);
              }}
              title={!isViewer ? "Click to edit title" : undefined}
            >
              {docTitle || detail.fileName}
            </span>
          )}
        </SheetTitle>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>{formatFileSize(detail.fileSize)}</span>
          <span>·</span>
          <span>Uploaded {formatDate(detail._creationTime)}</span>
          <span>·</span>
          <span>by {detail.uploaderName}</span>
        </div>
        <div className="flex items-center gap-2 pt-1">
          <ProcessingStatusBadge
            extractionStatus={detail.extractionStatus}
            classificationStatus={detail.classificationStatus}
            indexingStatus={detail.indexingStatus}
            showDetail
          />
          {detail.practitionerVerified && (
            <Badge
              color="emerald"
              variant="outline"
              size="sm"
              className="gap-1"
            >
              ✓ Verified
            </Badge>
          )}
          {/* Chunk count badge removed — count shown in Extracted tab via paginated loading */}
          <div className="ml-auto flex items-center gap-1.5">
            <DocumentExportMenu
              detail={detail}
              categoryExtractions={categoryExtractions}
            />
            {!isViewer && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRerunMetadata}
                  disabled={
                    isRerunningMetadata ||
                    detail.classificationStatus !== "classified"
                  }
                  className="gap-1 text-xs"
                  title="Re-run category-specific metadata extraction"
                >
                  {isRerunningMetadata ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <FileSearch2 className="h-3 w-3" />
                  )}
                  Re-extract Metadata
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReprocessAll}
                  disabled={isReprocessingAll}
                  className="gap-1 text-xs"
                  title="Re-run full pipeline (extract, classify, metadata, index)"
                >
                  {isReprocessingAll ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RotateCcw className="h-3 w-3" />
                  )}
                  Re-run All
                </Button>
              </>
            )}
          </div>
        </div>
      </SheetHeader>

      <Separator />

      <Tabs defaultValue={defaultTab} className="mt-4">
        <TabsList className="w-full">
          <TabsTrigger value="original" className="flex-1 gap-1 text-xs">
            <Eye className="h-3 w-3" />
            Original
          </TabsTrigger>
          <TabsTrigger value="metadata" className="flex-1 gap-1 text-xs">
            <Brain className="h-3 w-3" />
            Metadata
          </TabsTrigger>
          <TabsTrigger value="extracted" className="flex-1 gap-1 text-xs">
            <FileSearch className="h-3 w-3" />
            Extracted
          </TabsTrigger>
          <TabsTrigger value="timeline" className="flex-1 gap-1 text-xs">
            <Clock className="h-3 w-3" />
            Timeline
          </TabsTrigger>
        </TabsList>

        <TabsContent value="original" className="mt-4">
          <OriginalFilePreview
            url={detail.url}
            fileType={detail.fileType}
            fileName={detail.fileName}
            emailTextUrl={detail.emailTextUrl}
          />
        </TabsContent>

        <TabsContent value="metadata" className="mt-4">
          <DocumentMetadataPanel documentId={documentId} isViewer={isViewer} />
        </TabsContent>

        <TabsContent value="extracted" className="mt-4">
          <DocumentPreview documentId={documentId} />
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <ProcessingTimeline history={processingHistory} />
        </TabsContent>
      </Tabs>
    </>
  );
}

// ---------------------------------------------------------------------------
// Document Export Menu
// ---------------------------------------------------------------------------

type CategoryExtraction = {
  extractionCategory: string;
  status: string;
  extractedData?: unknown;
  extractedDataUrl?: string;
};

/** Resolve full extraction data — fetches from storage URL if needed, merges with inline. */
async function resolveExtractionData(
  ext: CategoryExtraction,
): Promise<Record<string, unknown> | null> {
  const inline = ext.extractedData as Record<string, unknown> | undefined;
  if (!ext.extractedDataUrl) return inline ?? null;

  try {
    const res = await fetch(ext.extractedDataUrl);
    if (!res.ok) return inline ?? null;
    const parsed: unknown = await res.json();
    // Stored blob is v.any(): only merge object payloads — an array or
    // primitive would corrupt the Excel walker's field lookups.
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed))
      return inline ?? null;
    const full = parsed as Record<string, unknown>;
    return inline ? { ...inline, ...full } : full;
  } catch {
    return inline ?? null;
  }
}

/** Format a label from a camelCase/snake_case key. */
function formatLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]/g, " ")
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
}

/** Build a complete export object from detail + resolved category extraction data (used by Word/PDF). */
function buildExportOutput(
  detail: DocumentDetailData,
  resolvedExtractions: Array<{
    category: string;
    data: Record<string, unknown>;
  }>,
): Record<string, unknown> {
  const output: Record<string, unknown> = {};

  const category = detail.category;
  if (category) output["Category"] = CATEGORY_LABELS[category] ?? category;

  const confidence =
    detail.classificationConfidence ?? detail.metadataConfidence;
  if (confidence !== undefined)
    output["Confidence"] = `${Math.round(confidence * 100)}%`;

  const meta: Record<string, unknown> | undefined = detail.structuredMetadata;
  if (meta) {
    if (meta.summary) output["Summary"] = meta.summary;
    if (meta.flags) output["Flags"] = meta.flags;
    if (meta.keyAmounts) output["Key Amounts"] = meta.keyAmounts;
    if (meta.keyDates) output["Key Dates"] = meta.keyDates;
    if (meta.keyParties) output["Key Parties"] = meta.keyParties;
    if (meta.entities) output["Entities"] = meta.entities;
    if (meta.metadata) output["Additional Metadata"] = meta.metadata;
  }

  if (detail.dateRangeCovered) output["Date Range"] = detail.dateRangeCovered;
  if (detail.partiesMentioned)
    output["Parties Mentioned"] = detail.partiesMentioned;
  if (!meta && detail.keyAmounts) output["Key Amounts"] = detail.keyAmounts;

  for (const ext of resolvedExtractions) {
    output[formatLabel(ext.category)] = ext.data;
  }

  return output;
}

// ---------------------------------------------------------------------------
// Excel export helpers — mirror GenericExtractionRenderer's data walking
// ---------------------------------------------------------------------------

const HIDDEN_EXPORT_KEYS = new Set([
  "relatedPartyStatus",
  "relatedPartyDetails",
  "isRelatedCreditor",
  "isRelatedParty",
  "relatedCreditor",
  "relationship",
  "relatedPartyAssessment",
  "source",
  "category",
  "extractionSchemaVersion",
]);

const COLUMN_ORDER: Record<string, string[]> = {
  relatedEntities: [
    "name",
    "identifiers",
    "relationship",
    "relationshipDescription",
  ],
  notedEntities: ["name", "identifiers", "role", "roleDescription"],
  officers: ["fullName", "dateOfBirth", "birthLocality", "appointments"],
  shareholders: ["entityName", "holderName", "holderType", "holdings"],
  changeHistory: [
    "effectiveDate",
    "changeType",
    "description",
    "previousValue",
    "newValue",
  ],
  registrations: [
    "registrationNumber",
    "securedParty",
    "grantor",
    "securityType",
    "collateralClass",
    "collateralType",
    "collateralDescription",
    "registrationDate",
    "registrationEndDate",
    "isPMSI",
    "isTransitional",
  ],
  employees: [
    "name",
    "startDate",
    "terminationDate",
    "employmentCategory",
    "annualSalary",
    "averageWeeklyHours",
    "wageRate",
    "payFrequency",
    "residencyStatus",
    "tfnPresent",
  ],
  entitlements: [
    "employeeName",
    "entitlementType",
    "leaveBalance",
    "amount",
    "asAtDate",
  ],
  creditorDetails: ["name", "abn", "address", "email"],
  creditor: ["name", "abn", "address", "email"],
  transactions: [
    "date",
    "description",
    "amount",
    "direction",
    "debit",
    "credit",
    "balanceAfter",
    "reference",
    "counterpartyName",
    "transactionType",
  ],
  rawEntries: [
    "date",
    "accountCode",
    "accountName",
    "description",
    "debit",
    "credit",
    "balance",
    "reference",
    "journal",
  ],
};

function getOrderedExportKeys(
  sectionKey: string,
  discoveredKeys: string[],
): string[] {
  const preferred = COLUMN_ORDER[sectionKey];
  if (!preferred) return discoveredKeys;
  const ordered = preferred.filter((k) => discoveredKeys.includes(k));
  const remaining = discoveredKeys.filter((k) => !preferred.includes(k));
  return [...ordered, ...remaining];
}

/** Flatten a cell value for Excel — handles nested objects and amounts. */
function flattenCellForExcel(v: unknown): string | number {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "string") return v;
  if (Array.isArray(v)) {
    if (v.length === 0) return "";
    if (typeof v[0] === "string" || typeof v[0] === "number")
      return v.join(", ");
    if (typeof v[0] === "object" && v[0] !== null) {
      // Array of identifiers → "ACN: 123, ABN: 456"
      const first = v[0] as Record<string, unknown>;
      if ("type" in first && "value" in first) {
        return v
          .map((entry) => {
            const id = entry as Record<string, unknown>;
            return `${String(id.type)}: ${String(id.value)}`;
          })
          .join(", ");
      }
      // Array of appointments → "Director (appointed 01/01/2024)"
      if ("role" in first) {
        return v
          .map((entry) => {
            const a = entry as Record<string, unknown>;
            const parts = [formatLabel(String(a.role ?? ""))];
            if (a.appointedDate)
              parts.push(`appointed ${String(a.appointedDate)}`);
            if (a.ceasedDate) parts.push(`ceased ${String(a.ceasedDate)}`);
            return parts.join(", ");
          })
          .join("; ");
      }
    }
    return JSON.stringify(v);
  }
  if (typeof v === "object") {
    const obj = v as Record<string, unknown>;
    // Amount object → numeric value
    if ("amount" in obj && typeof obj.amount === "number") return obj.amount;
    // Period object
    if ("from" in obj || "to" in obj) {
      const parts: string[] = [];
      if (obj.from) parts.push(String(obj.from));
      if (obj.to) parts.push(String(obj.to));
      return parts.join(" — ");
    }
    // Identifier
    if ("type" in obj && "value" in obj) return `${obj.type}: ${obj.value}`;
    // Leave balance
    if ("quantity" in obj && "unit" in obj)
      return `${obj.quantity} ${obj.unit}`;
    return JSON.stringify(obj);
  }
  return String(v);
}

/**
 * Build an ExportSheet from an array of objects.
 * Discovers all keys, applies column ordering, formats headers nicely.
 */
function buildArraySheet(
  items: Record<string, unknown>[],
  sheetName: string,
  sectionKey?: string,
): ExportSheet {
  const allKeys = new Set<string>();
  for (const item of items) {
    for (const k of Object.keys(item)) {
      if (!HIDDEN_EXPORT_KEYS.has(k)) allKeys.add(k);
    }
  }
  const rawKeys = Array.from(allKeys);
  const colKeys = sectionKey
    ? getOrderedExportKeys(sectionKey, rawKeys)
    : rawKeys;

  const headers = colKeys.map((h) => formatLabel(h));
  const rows = items.map((item) =>
    colKeys.map((k) => flattenCellForExcel(item[k])),
  );

  return { name: sheetName, headers, rows };
}

/** Ensure all sheet names are unique and within the 31-char Excel limit. */
function deduplicateSheetNames(sheets: ExportSheet[]): void {
  const usedNames = new Set<string>();

  for (const sheet of sheets) {
    let candidate = sheet.name.slice(0, 31);
    let suffix = 2;
    while (usedNames.has(candidate)) {
      const tag = ` (${suffix})`;
      candidate = sheet.name.slice(0, 31 - tag.length) + tag;
      suffix++;
    }
    sheet.name = candidate;
    usedNames.add(candidate);
  }
}

/**
 * Recursively walks extraction data and collects all array-of-objects
 * sections as Excel sheets, plus scalar fields as overview key-value sheets.
 * Mirrors how GenericExtractionRenderer walks the data.
 *
 * Uses section names directly (e.g. "Declared Assets") rather than
 * prefixing with the category label, to stay within Excel's 31-char limit.
 * Deduplication is handled separately via `deduplicateSheetNames`.
 */
function collectExtractionSheets(
  data: Record<string, unknown>,
  fallbackName: string,
  sheets: ExportSheet[],
) {
  const scalarRows: (string | number | null)[][] = [];

  for (const [key, value] of Object.entries(data)) {
    if (value === null || value === undefined || value === "") continue;
    if (HIDDEN_EXPORT_KEYS.has(key)) continue;

    // Array of objects → dedicated tabular sheet
    if (
      Array.isArray(value) &&
      value.length > 0 &&
      typeof value[0] === "object" &&
      value[0] !== null
    ) {
      sheets.push(
        buildArraySheet(
          value as Record<string, unknown>[],
          formatLabel(key),
          key,
        ),
      );
      continue;
    }

    // Array of primitives → scalar row
    if (Array.isArray(value)) {
      if (value.length > 0) {
        scalarRows.push([formatLabel(key), value.map(String).join(", ")]);
      }
      continue;
    }

    // Nested object → recurse into it to find more arrays
    if (typeof value === "object") {
      const obj = value as Record<string, unknown>;

      // Amount object → scalar row with number
      if ("amount" in obj && typeof obj.amount === "number") {
        scalarRows.push([formatLabel(key), obj.amount]);
        continue;
      }
      // Period object → scalar row
      if (("from" in obj || "to" in obj) && Object.keys(obj).length <= 3) {
        const parts: string[] = [];
        if (obj.from) parts.push(String(obj.from));
        if (obj.to) parts.push(String(obj.to));
        scalarRows.push([formatLabel(key), parts.join(" — ")]);
        continue;
      }
      // Identifier → scalar row
      if ("type" in obj && "value" in obj && typeof obj.type === "string") {
        scalarRows.push([formatLabel(key), `${obj.type}: ${obj.value}`]);
        continue;
      }

      collectExtractionSheets(obj, formatLabel(key), sheets);
      continue;
    }

    // Primitive → scalar row
    scalarRows.push([formatLabel(key), flattenCellForExcel(value)]);
  }

  if (scalarRows.length > 0) {
    sheets.push({
      name: fallbackName,
      headers: ["Field", "Value"],
      rows: scalarRows,
    });
  }
}

/**
 * Build structured Excel sheets that mirror the metadata panel layout.
 * Each metadata section and extraction section gets its own sheet.
 */
function buildExcelSheets(
  detail: DocumentDetailData,
  resolvedExtractions: Array<{
    category: string;
    data: Record<string, unknown>;
  }>,
): ExportSheet[] {
  const sheets: ExportSheet[] = [];

  // --- Sheet 1: Overview (key-value pairs) ---
  const overviewRows: (string | number | null)[][] = [];

  const docCategory = detail.category;
  if (docCategory) {
    overviewRows.push([
      "Category",
      CATEGORY_LABELS[docCategory] ?? docCategory,
    ]);
  }

  const confidence =
    detail.classificationConfidence ?? detail.metadataConfidence;
  if (confidence !== undefined) {
    overviewRows.push(["Confidence", `${Math.round(confidence * 100)}%`]);
  }

  if (detail.fileName) overviewRows.push(["File Name", detail.fileName]);
  if (detail.fileSize)
    overviewRows.push(["File Size", String(detail.fileSize)]);
  if (detail.uploaderName)
    overviewRows.push(["Uploaded By", detail.uploaderName]);

  // Document-level structured metadata summary
  const meta: Record<string, unknown> | undefined = detail.structuredMetadata;
  const hasNewMeta = meta && typeof meta === "object" && "summary" in meta;
  if (hasNewMeta && meta.summary) {
    overviewRows.push(["Summary", String(meta.summary)]);
  }

  // Legacy date range
  const dateRange = detail.dateRangeCovered;
  if (dateRange) {
    overviewRows.push([
      "Date Range",
      `${dateRange.start ?? "?"} — ${dateRange.end ?? "?"}`,
    ]);
  }

  // Legacy parties mentioned
  const parties = detail.partiesMentioned;
  if (parties && parties.length > 0) {
    overviewRows.push(["Parties Mentioned", parties.join(", ")]);
  }

  if (overviewRows.length > 0) {
    sheets.push({
      name: "Overview",
      headers: ["Field", "Value"],
      rows: overviewRows,
    });
  }

  // --- Document-level structured metadata sections ---
  const normalizedMeta = hasNewMeta ? coerceStructuredMetadata(meta) : null;
  if (normalizedMeta) {
    if (normalizedMeta.flags.length > 0) {
      sheets.push({
        name: "Flags",
        headers: ["Flag", "Severity", "Detail"],
        rows: normalizedMeta.flags.map((f) => [f.flag, f.severity, f.detail]),
      });
    }

    if (normalizedMeta.keyAmounts.length > 0) {
      const sorted = [...normalizedMeta.keyAmounts].sort(
        (a, b) => Math.abs(b.amount) - Math.abs(a.amount),
      );
      sheets.push({
        name: "Key Amounts",
        headers: ["Description", "Amount", "Context"],
        rows: sorted.map((item) => [
          item.label,
          item.amount,
          item.context ?? "",
        ]),
      });
    }

    if (normalizedMeta.keyDates.length > 0) {
      const sorted = [...normalizedMeta.keyDates].sort((a, b) =>
        a.date.localeCompare(b.date),
      );
      sheets.push({
        name: "Key Dates",
        headers: ["Event", "Date"],
        rows: sorted.map((item) => [item.label, item.date]),
      });
    }

    if (normalizedMeta.keyParties.length > 0) {
      sheets.push({
        name: "Key Parties",
        headers: ["Name", "Role"],
        rows: normalizedMeta.keyParties.map((p) => [p.name, p.role]),
      });
    }

    const { entities } = normalizedMeta;
    if (
      entities.primaryEntity ||
      (entities.relatedEntities && entities.relatedEntities.length > 0)
    ) {
      const entityRows: (string | null)[][] = [];
      if (entities.primaryEntity)
        entityRows.push([entities.primaryEntity, "Primary"]);
      if (entities.relatedEntities) {
        for (const e of entities.relatedEntities)
          entityRows.push([e, "Related"]);
      }
      sheets.push({
        name: "Entities",
        headers: ["Entity", "Type"],
        rows: entityRows,
      });
    }

    const additionalMeta = normalizedMeta.metadata;
    if (additionalMeta && Object.keys(additionalMeta).length > 0) {
      const metaRows: (string | number | null)[][] = [];
      for (const [key, value] of Object.entries(additionalMeta)) {
        if (value === null || value === undefined) continue;
        const cellValue =
          typeof value === "boolean" ? (value ? "Yes" : "No") : value;
        metaRows.push([formatLabel(key), cellValue]);
      }
      if (metaRows.length > 0) {
        sheets.push({
          name: "Additional Metadata",
          headers: ["Field", "Value"],
          rows: metaRows,
        });
      }
    }
  }

  // Legacy key amounts (only when no new structured metadata)
  if (!hasNewMeta) {
    const legacyAmounts = detail.keyAmounts;
    if (legacyAmounts && legacyAmounts.length > 0) {
      sheets.push({
        name: "Key Amounts",
        headers: ["Description", "Amount"],
        rows: legacyAmounts.map((item) => [item.label, item.amount]),
      });
    }
  }

  // --- Category extraction sheets ---
  // Walk each extraction's data recursively (mirroring GenericExtractionRenderer)
  for (const ext of resolvedExtractions) {
    const categoryLabel = formatLabel(ext.category);
    const data = ext.data;

    // Extract base fields into an overview sheet for this category
    const baseOverview: (string | number | null)[][] = [];

    if (typeof data.summary === "string" && data.summary) {
      baseOverview.push(["Summary", data.summary]);
    }

    const pe = data.primaryEntity as
      | { name?: string; identifiers?: Array<{ type: string; value: string }> }
      | undefined;
    if (pe && typeof pe === "object" && pe.name) {
      baseOverview.push(["Primary Entity", pe.name]);
      if (pe.identifiers && pe.identifiers.length > 0) {
        baseOverview.push([
          "Identifiers",
          pe.identifiers.map((id) => `${id.type}: ${id.value}`).join(", "),
        ]);
      }
    }

    if (baseOverview.length > 0) {
      sheets.push({
        name: `${categoryLabel} Overview`,
        headers: ["Field", "Value"],
        rows: baseOverview,
      });
    }

    // Base-level dates → sheet
    const dates = data.dates as
      | Array<{ label: string; date: string; isApproximate?: boolean }>
      | undefined;
    if (Array.isArray(dates) && dates.length > 0) {
      sheets.push({
        name: `${categoryLabel} Dates`,
        headers: ["Event", "Date", "Approximate"],
        rows: dates.map((d) => [d.label, d.date, d.isApproximate ? "Yes" : ""]),
      });
    }

    // Base-level amounts → sheet
    const amounts = data.amounts as
      Array<{ label: string; amount: number; currency?: string }> | undefined;
    if (Array.isArray(amounts) && amounts.length > 0) {
      sheets.push({
        name: `${categoryLabel} Amounts`,
        headers: ["Description", "Amount", "Currency"],
        rows: amounts.map((a) => [a.label, a.amount, a.currency ?? "AUD"]),
      });
    }

    // Walk category-specific data (everything except base fields)
    const baseKeys = new Set(["summary", "primaryEntity", "dates", "amounts"]);
    const categoryDataKeys = Object.keys(data).filter(
      (k) => !baseKeys.has(k) && !HIDDEN_EXPORT_KEYS.has(k),
    );

    for (const dataKey of categoryDataKeys) {
      const value = data[dataKey];
      if (value === null || value === undefined) continue;

      // Top-level array of objects
      if (
        Array.isArray(value) &&
        value.length > 0 &&
        typeof value[0] === "object" &&
        value[0] !== null
      ) {
        sheets.push(
          buildArraySheet(
            value as Record<string, unknown>[],
            formatLabel(dataKey),
            dataKey,
          ),
        );
        continue;
      }

      // Nested object — recurse into it
      if (typeof value === "object" && !Array.isArray(value)) {
        collectExtractionSheets(
          value as Record<string, unknown>,
          categoryLabel,
          sheets,
        );
        continue;
      }
    }
  }

  deduplicateSheetNames(sheets);
  return sheets;
}

function DocumentExportMenu({
  detail,
  categoryExtractions,
}: {
  detail: DocumentDetailData;
  categoryExtractions?: CategoryExtraction[];
}) {
  const [exporting, setExporting] = useState<string | null>(null);

  const title = detail.documentType || detail.fileName || "Document";
  const baseName = title
    .replace(/\.[^/.]+$/, "")
    .replace(/[^a-zA-Z0-9\s\-_]/g, "")
    .replace(/\s+/g, "_")
    .slice(0, 100);

  const handleExport = async (format: "word" | "pdf" | "excel") => {
    setExporting(format);
    try {
      const completedExtractions = (categoryExtractions ?? []).filter(
        (e) => e.status === "completed",
      );
      const resolvedExtractions = await Promise.all(
        completedExtractions.map(async (ext) => {
          const data = await resolveExtractionData(ext);
          return data ? { category: ext.extractionCategory, data } : null;
        }),
      );
      const extractions = resolvedExtractions.filter(
        (e): e is NonNullable<typeof e> => e !== null,
      );

      const { blob, ext } = await (async () => {
        switch (format) {
          case "word": {
            const output = buildExportOutput(detail, extractions);
            return { blob: await exportToWord({ title, output }), ext: "docx" };
          }
          case "pdf": {
            const output = buildExportOutput(detail, extractions);
            return { blob: await exportToPdf({ title, output }), ext: "pdf" };
          }
          case "excel": {
            const sheets = buildExcelSheets(detail, extractions);
            return { blob: exportToExcel({ title, sheets }), ext: "xlsx" };
          }
        }
      })();

      saveAs(blob, `${baseName}.${ext}`);
    } catch (err) {
      console.error(`Export to ${format} failed:`, err);
      toast.error(`Failed to export as ${format}`);
    } finally {
      setExporting(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1 text-xs"
          disabled={exporting !== null}
        >
          {exporting ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Download className="h-3 w-3" />
          )}
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem
          onClick={() => handleExport("word")}
          disabled={exporting !== null}
          className="gap-2 text-xs"
        >
          <FileText className="h-3.5 w-3.5 text-muted-foreground" />
          Word (.docx)
          {exporting === "word" && (
            <Loader2 className="ml-auto h-3 w-3 animate-spin" />
          )}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleExport("pdf")}
          disabled={exporting !== null}
          className="gap-2 text-xs"
        >
          <FileText className="h-3.5 w-3.5 text-muted-foreground" />
          PDF (.pdf)
          {exporting === "pdf" && (
            <Loader2 className="ml-auto h-3 w-3 animate-spin" />
          )}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleExport("excel")}
          disabled={exporting !== null}
          className="gap-2 text-xs"
        >
          <FileText className="h-3.5 w-3.5 text-muted-foreground" />
          Excel (.xlsx)
          {exporting === "excel" && (
            <Loader2 className="ml-auto h-3 w-3 animate-spin" />
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Original File Preview
// ---------------------------------------------------------------------------

/** Detect file type category from MIME type + extension */
function getFilePreviewType(fileType: string, fileName: string) {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";

  if (fileType.startsWith("image/")) return "image" as const;
  if (fileType === "application/pdf") return "pdf" as const;

  // Email containers — no browser can render these, so the preview falls back
  // to the plain-text rendition produced by the extraction stage (MOR-899).
  if (
    fileType === "message/rfc822" ||
    fileType === "application/vnd.ms-outlook" ||
    ext === "eml" ||
    ext === "msg"
  )
    return "email" as const;

  // Word documents
  if (
    fileType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    fileType === "application/msword" ||
    ext === "docx" ||
    ext === "doc" ||
    ext === "dotx" ||
    ext === "dot"
  )
    return "docx" as const;

  // Excel spreadsheets
  if (
    fileType ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    fileType === "application/vnd.ms-excel" ||
    ext === "xlsx" ||
    ext === "xls"
  )
    return "xlsx" as const;

  // CSV / TSV
  if (
    fileType === "text/csv" ||
    fileType === "text/tab-separated-values" ||
    ext === "csv" ||
    ext === "tsv"
  )
    return "csv" as const;

  // Plain text types
  if (
    fileType.startsWith("text/") ||
    fileType === "application/json" ||
    fileType === "application/xml" ||
    ["txt", "json", "md", "log", "xml", "yaml", "yml"].includes(ext)
  )
    return "text" as const;

  return "unknown" as const;
}

/**
 * Type-aware renderer for an original file: PDF/image natively, Word via
 * mammoth, Excel via SheetJS, CSV/text inline. Exported for reuse by the
 * mail attachment preview (MOR-783).
 */
export function OriginalFilePreview({
  url,
  fileType,
  fileName,
  emailTextUrl = null,
}: {
  url: string | null;
  fileType: string;
  fileName: string;
  /** Plain-text rendition of a parsed email, used to preview .eml/.msg files. */
  emailTextUrl?: string | null;
}) {
  if (!url) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FileText className="h-8 w-8 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">
          Original file not available.
        </p>
      </div>
    );
  }

  const previewType = getFilePreviewType(fileType, fileName);

  return (
    <div className="flex flex-col gap-3">
      {/* Download bar — always visible */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{fileType}</span>
        <a href={url} target="_blank" rel="noopener noreferrer">
          <Button variant="outline" size="sm" className="gap-1.5 text-xs">
            <Download className="h-3 w-3" />
            Download original
          </Button>
        </a>
      </div>

      {/* Image */}
      {previewType === "image" && (
        <div className="overflow-hidden rounded-lg border bg-muted/30">
          <img
            src={url}
            alt={fileName}
            className="h-auto max-h-[75vh] w-full object-contain"
          />
        </div>
      )}

      {/* PDF */}
      {previewType === "pdf" && (
        <div className="overflow-hidden rounded-lg border bg-muted/30">
          <iframe
            src={url}
            title={fileName}
            className="w-full border-0"
            style={{ height: "80vh" }}
          />
        </div>
      )}

      {/* Word docs — mammoth */}
      {previewType === "docx" && <DocxPreview url={url} />}

      {/* Excel — SheetJS */}
      {previewType === "xlsx" && <XlsxPreview url={url} />}

      {/* CSV / TSV */}
      {previewType === "csv" && <CsvPreview url={url} fileName={fileName} />}

      {/* Plain text */}
      {previewType === "text" && <TextFilePreview url={url} />}

      {/* Email (.eml / .msg) */}
      {previewType === "email" && <EmailPreview textUrl={emailTextUrl} />}

      {/* Unknown */}
      {previewType === "unknown" && (
        <div className="flex flex-col items-center justify-center rounded-lg border bg-muted/20 py-12 text-center">
          <FileText className="h-10 w-10 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">
            Preview not available for .
            {fileName.split(".").pop()?.toLowerCase()} files
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Download the file to open it, or check the{" "}
            <span className="font-medium text-foreground">Extracted</span> tab.
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared loading / error states
// ---------------------------------------------------------------------------

function PreviewLoading() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

function PreviewError({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border bg-muted/20 py-12 text-center">
      <AlertCircle className="h-8 w-8 text-muted-foreground" />
      <p className="mt-2 text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DOCX Preview (mammoth)
// ---------------------------------------------------------------------------

function DocxPreview({ url }: { url: string }) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error("fetch failed");
        return res.arrayBuffer();
      })
      .then((arrayBuffer) => mammoth.convertToHtml({ arrayBuffer }))
      .then((result) => {
        if (cancelled) return;
        setHtml(DOMPurify.sanitize(result.value));
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  if (loading) return <PreviewLoading />;
  if (error || html === null)
    return <PreviewError message="Could not render Word document." />;

  return (
    <>
      <div
        className="docx-preview max-h-[80vh] overflow-auto rounded-lg border bg-white p-6"
        // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- html state is only ever set to DOMPurify.sanitize(mammoth output) in the fetch effect above
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <style>{`
        .docx-preview { color: #1a1a1a; font-family: system-ui, sans-serif; font-size: 0.875rem; line-height: 1.7; }
        .docx-preview h1 { font-size: 1.5rem; font-weight: 700; margin: 1.5rem 0 0.75rem; }
        .docx-preview h2 { font-size: 1.25rem; font-weight: 600; margin: 1.25rem 0 0.5rem; }
        .docx-preview h3 { font-size: 1.1rem; font-weight: 600; margin: 1rem 0 0.5rem; }
        .docx-preview p { margin: 0.5rem 0; }
        .docx-preview ul, .docx-preview ol { padding-left: 1.5rem; margin: 0.5rem 0; }
        .docx-preview li { margin: 0.25rem 0; }
        .docx-preview table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 0.8rem; }
        .docx-preview th, .docx-preview td { border: 1px solid #e5e7eb; padding: 0.5rem 0.75rem; text-align: left; }
        .docx-preview th { background: #f9fafb; font-weight: 600; }
        .docx-preview tr:hover td { background: #f9fafb; }
        .docx-preview img { max-width: 100%; height: auto; border-radius: 0.375rem; }
        .docx-preview strong, .docx-preview b { font-weight: 600; }
      `}</style>
    </>
  );
}

// ---------------------------------------------------------------------------
// XLSX Preview (SheetJS)
// ---------------------------------------------------------------------------

function XlsxPreview({ url }: { url: string }) {
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [activeSheet, setActiveSheet] = useState(0);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error("fetch failed");
        return res.arrayBuffer();
      })
      .then((arrayBuffer) => {
        if (cancelled) return;
        const wb = XLSX.read(arrayBuffer, { type: "array" });
        setWorkbook(wb);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  const sheetData = useMemo(() => {
    if (!workbook) return null;
    const sheetName = workbook.SheetNames[activeSheet];
    if (!sheetName) return null;
    const ws = workbook.Sheets[sheetName];
    if (!ws) return null;
    const rows: string[][] = XLSX.utils.sheet_to_json(ws, {
      header: 1,
      defval: "",
    });
    return rows;
  }, [workbook, activeSheet]);

  if (loading) return <PreviewLoading />;
  if (error || !workbook)
    return <PreviewError message="Could not render spreadsheet." />;

  const sheetNames = workbook.SheetNames;
  const rows = sheetData ?? [];
  const headers = rows[0] ?? [];
  const dataRows = rows.slice(1);
  const maxRows = 500;
  const truncated = dataRows.length > maxRows;
  const displayRows = truncated ? dataRows.slice(0, maxRows) : dataRows;

  return (
    <div className="flex flex-col gap-2">
      {/* Sheet tabs */}
      {sheetNames.length > 1 && (
        <div className="flex flex-wrap gap-1">
          {sheetNames.map((name, idx) => (
            <button
              key={name}
              onClick={() => setActiveSheet(idx)}
              className={`rounded-md border px-3 py-1 text-xs transition-colors ${
                idx === activeSheet
                  ? "border-foreground bg-foreground text-background"
                  : "border-border bg-muted/50 text-muted-foreground hover:bg-muted"
              }`}
            >
              {name}
            </button>
          ))}
        </div>
      )}

      <span className="text-[10px] text-muted-foreground">
        {dataRows.length} row{dataRows.length !== 1 ? "s" : ""} ·{" "}
        {headers.length} column{headers.length !== 1 ? "s" : ""}
        {truncated && ` (showing first ${maxRows})`}
      </span>

      <div className="max-h-[75vh] overflow-auto rounded-lg border">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10">
            <tr className="bg-muted">
              {headers.map((h, i) => (
                <th
                  key={i}
                  className="whitespace-nowrap border-b-2 border-border px-3 py-2 text-left text-[10px] font-medium uppercase tracking-wider text-muted-foreground"
                >
                  {String(h) || `Col ${i + 1}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, ri) => (
              <tr
                key={ri}
                className="border-t border-border/50 transition-colors hover:bg-muted/30"
              >
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className="max-w-[300px] truncate whitespace-nowrap px-3 py-1.5"
                  >
                    {String(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CSV Preview
// ---------------------------------------------------------------------------

function CsvPreview({ url, fileName }: { url: string; fileName: string }) {
  const [rows, setRows] = useState<string[][] | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch");
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        const delimiter = fileName.endsWith(".tsv") ? "\t" : ",";
        const parsed = parseCsv(text, delimiter);
        setRows(parsed);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url, fileName]);

  if (loading) return <PreviewLoading />;
  if (error || !rows || rows.length === 0)
    return <PreviewError message="Could not load CSV preview." />;

  const headers = rows[0];
  const dataRows = rows.slice(1);
  const maxRows = 500;
  const truncated = dataRows.length > maxRows;
  const displayRows = truncated ? dataRows.slice(0, maxRows) : dataRows;

  return (
    <div className="flex flex-col gap-2">
      <span className="text-[10px] text-muted-foreground">
        {dataRows.length} row{dataRows.length !== 1 ? "s" : ""} ·{" "}
        {headers.length} column{headers.length !== 1 ? "s" : ""}
        {truncated && ` (showing first ${maxRows})`}
      </span>
      <div className="max-h-[75vh] overflow-auto rounded-lg border">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10">
            <tr className="bg-muted">
              {headers.map((h, i) => (
                <th
                  key={i}
                  className="whitespace-nowrap border-b-2 border-border px-3 py-2 text-left text-[10px] font-medium uppercase tracking-wider text-muted-foreground"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, ri) => (
              <tr
                key={ri}
                className="border-t border-border/50 transition-colors hover:bg-muted/30"
              >
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className="max-w-[300px] truncate whitespace-nowrap px-3 py-1.5"
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Simple CSV parser that handles quoted fields */
function parseCsv(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  const lines = text.split("\n");

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const cells: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < trimmed.length; i++) {
      const char = trimmed[i];
      if (inQuotes) {
        if (char === '"') {
          if (i + 1 < trimmed.length && trimmed[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          current += char;
        }
      } else if (char === '"') {
        inQuotes = true;
      } else if (char === delimiter) {
        cells.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    cells.push(current.trim());
    rows.push(cells);
  }

  return rows;
}

// ---------------------------------------------------------------------------
// Text File Preview
// ---------------------------------------------------------------------------

/**
 * Previews an email container (.eml / .msg) from the plain-text rendition the
 * extraction stage wrote to storage. The rendition starts with a header block
 * (`[Email: …]`, `From:`, `To:`, …) followed by a blank line and the body, so
 * the headers are split back out and shown as fields above the message.
 */
function EmailPreview({ textUrl }: { textUrl: string | null }) {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!textUrl) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);

    fetch(textUrl)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch");
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        setContent(text);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [textUrl]);

  if (!textUrl && !loading) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border bg-muted/20 py-12 text-center">
        <Mail className="h-10 w-10 text-muted-foreground" />
        <p className="mt-3 text-sm text-muted-foreground">
          Email not parsed yet
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          The preview appears once processing finishes. Download the file to
          open it in Outlook in the meantime.
        </p>
      </div>
    );
  }

  if (loading) return <PreviewLoading />;
  if (error || content === null)
    return <PreviewError message="Could not load email preview." />;

  const { headers, body } = splitEmailRendition(content);

  return (
    <div className="max-h-[80vh] overflow-auto rounded-lg border bg-background">
      {headers.length > 0 && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 border-b bg-muted/30 px-4 py-3 text-xs">
          {headers.map(({ label, value }) => (
            <Fragment key={label}>
              <dt className="font-medium text-muted-foreground">{label}</dt>
              <dd className="break-words text-foreground">{value}</dd>
            </Fragment>
          ))}
        </dl>
      )}
      <pre className="whitespace-pre-wrap break-words px-4 py-3 font-sans text-xs leading-relaxed">
        {body}
      </pre>
    </div>
  );
}

/**
 * Splits a `renderEmailAsText` rendition into display headers and the body.
 * The leading `[Email: <filename>]` marker is dropped — the filename is
 * already in the dialog title.
 */
function splitEmailRendition(text: string): {
  headers: { label: string; value: string }[];
  body: string;
} {
  const blankLine = text.indexOf("\n\n");
  if (blankLine === -1) return { headers: [], body: text };

  const headers: { label: string; value: string }[] = [];
  for (const line of text.slice(0, blankLine).split("\n")) {
    if (line.startsWith("[Email:")) continue;
    const colon = line.indexOf(": ");
    if (colon === -1) continue;
    headers.push({
      label: line.slice(0, colon),
      value: line.slice(colon + 2),
    });
  }

  return { headers, body: text.slice(blankLine + 2) };
}

function TextFilePreview({ url }: { url: string }) {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch");
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        setContent(text);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  if (loading) return <PreviewLoading />;
  if (error || content === null)
    return <PreviewError message="Could not load file preview." />;

  return (
    <div className="max-h-[80vh] overflow-auto rounded-lg border bg-muted/30">
      <pre className="whitespace-pre-wrap break-words p-4 font-mono text-xs leading-relaxed">
        {content}
      </pre>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Processing Timeline
// ---------------------------------------------------------------------------

/** Checks if an error message indicates a permanent, non-retriable failure. */
function isErrorPermanent(error: string): boolean {
  const permanent = [
    "Password-protected",
    "Corrupt or unreadable",
    "File too large",
    "cannot be parsed",
    "unsupported format",
    "appears to be binary",
    "DOCUMENT_PASSWORD_PROTECTED",
    "DOCUMENT_CORRUPT",
  ];
  return permanent.some((p) => error.includes(p));
}

/** Cleans up raw API error JSON into a readable message. */
function formatErrorMessage(error: string): string {
  // Try to extract message from JSON error responses like: 422 {"error":{"message":"..."}}
  const jsonMatch = error.match(/\d{3}\s*(\{.+\})/);
  if (jsonMatch) {
    try {
      const parsed: { error?: { message?: string }; detail?: string } =
        JSON.parse(jsonMatch[1]);
      return parsed.error?.message ?? parsed.detail ?? error;
    } catch {
      // Fall through to raw message
    }
  }
  return error;
}

function ProcessingTimeline({
  history,
}: {
  history:
    | Array<{
        _id: string;
        _creationTime: number;
        stage: string;
        status: string;
        startedAt?: number;
        completedAt?: number;
        error?: string;
        retryCount: number;
      }>
    | undefined;
}) {
  if (!history || history.length === 0) {
    return (
      <div className="py-4 text-center text-sm text-muted-foreground">
        No processing history available.
      </div>
    );
  }

  const stageLabels: Record<string, string> = {
    extraction: "Document Extraction",
    classification: "Classification & Metadata",
    indexing: "Embedding & Indexing",
  };

  const statusColorMap: Record<string, BadgeColor | undefined> = {
    pending: undefined,
    running: "blue",
    completed: "emerald",
    failed: "red",
  };

  return (
    <div className="flex flex-col gap-3">
      {history.map((item) => {
        const errorMsg = item.error ? formatErrorMessage(item.error) : null;
        const permanent = errorMsg ? isErrorPermanent(errorMsg) : false;

        return (
          <div
            key={item._id}
            className="flex flex-col gap-1 rounded-lg border p-3"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {stageLabels[item.stage] ?? item.stage}
              </span>
              <Badge
                variant="soft"
                size="sm"
                color={statusColorMap[item.status]}
              >
                {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
              </Badge>
            </div>

            <div className="flex gap-4 text-[10px] text-muted-foreground">
              {item.startedAt && (
                <span>
                  Started: {new Date(item.startedAt).toLocaleString()}
                </span>
              )}
              {item.completedAt && (
                <span>
                  Completed: {new Date(item.completedAt).toLocaleString()}
                </span>
              )}
              {item.startedAt && item.completedAt && (
                <span>
                  Duration:{" "}
                  {((item.completedAt - item.startedAt) / 1000).toFixed(1)}s
                </span>
              )}
            </div>

            {errorMsg && (
              <div
                className={`mt-1 rounded p-2.5 text-xs ${
                  permanent
                    ? "border border-red-200 bg-red-50 text-red-800"
                    : "border border-amber-200 bg-amber-50 text-amber-800"
                }`}
              >
                <div className="flex items-start gap-2">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <div>
                    <span className="font-medium">
                      {permanent ? "Permanent error" : "Retriable error"}
                    </span>
                    <p className="mt-0.5">{errorMsg}</p>
                    {permanent && (
                      <p className="mt-1 text-[10px] opacity-70">
                        This error cannot be resolved by retrying. The file may
                        need to be re-uploaded in a different format.
                      </p>
                    )}
                    {!permanent && (
                      <p className="mt-1 text-[10px] opacity-70">
                        This may resolve on retry. Use the reprocess button to
                        try again.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {item.retryCount > 0 && (
              <span className="text-[10px] text-muted-foreground">
                Retry #{item.retryCount}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
